<template>
  <div>
    <button
        class="flex items-center gap-1.5 rounded-lg border border-[#E7E7E7] px-2 py-1 transition-colors hover:bg-secondary/10 text-3xl"
        @click="openModal = true"
    >
      <Icon name="flag_circle" class="text-secondary" is-filled />
    </button>
    <Teleport to="body">
    <Modal size="medium" :open="openModal" @close="handleClose">
      <div class="relative -m-6 bg-[#FAFAFA] sm:p-8 p-4 lg:p-12">
        <button class="absolute right-4 top-4" @click="handleClose">
          <Icon name="close" class="text-xl text-zinc-800" />
        </button>

        <div class="flex items-center gap-2.5 mb-3">
          <Icon name="flag_circle" class="text-primary text-3xl" is-filled />
          <h2 class="text-xl font-normal text-zinc-800">{{ __('Report misconduct') }}</h2>
        </div>

        <hr class="border-t border-primary mb-8" />

        <form @submit.prevent="handleSubmit">
          <div class="mb-6">
            <InputTextarea
                id="description"
                v-model:value="description"
                v-model:errors="errors.description"
                class="md:col-span-2"
                :placeholder="__('Description of the misconduct')"
            />
          </div>

          <div class="flex items-center space-x-2 mb-6">
            <InputCheckbox
                id="anonymous"
                v-model:value="isAnonymous"
                :label="__('I want to remain anonymous')"
            />
          </div>

          <div class="text-sm text-zinc-500">
            <p class="bold">{{ __('Note:')}} </p>
            <p class="mt-4"> {{ __('Please provide the following information when reporting a misconduct:')}}</p>
            <ul class="list-disc pl-6 space-y-3 mt-3">
              <li><strong>{{__('What')}}</strong> {{ __('alleged wrongdoing are you reporting? Describe what happened, including as many details as possible that you know.')}}</li>
              <li><strong>{{__('Who')}}</strong> {{ __('committed the alleged wrongdoing? Do you know if anyone else was involved? Provide full names, titles and organization, if possible.')}}</li>
              <li><strong>{{__('When')}}</strong> {{__('and ')}}<strong>{{__('where')}}</strong> {{ __('did the incident(s) happen? Indicate date(s) and time(s), if available.')}}</li>
              <li><strong>{{__('How')}}</strong> {{ __('did the individual commit the alleged wrongdoing?')}}</li>
              <li><strong>{{__('Why')}}</strong> {{ __('do you believe the activity was improper?')}}</li>
            </ul>
          </div>

          <div
              class="mt-8 flex flex-row gap-2 md:flex-row-reverse justify-center items-center"
          >
            <Button
                class="flex items-center justify-center gap-1.5"
                :type="isLoading ? 'button' : 'submit'"
                :disabled="isLoading"
            >
              <Icon
                  v-if="isLoading"
                  class="animate-spin text-lg"
                  name="progress_activity"
              />
              <span>{{ __('Send') }}</span>
            </Button>
            <Button variant="ghost" @click="handleCancelButtonClick">
              <span>{{ __('Cancel') }}</span>
            </Button>
          </div>
        </form>
      </div>
    </Modal>
    </Teleport>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import axios from 'axios';
import { Modal } from '@/components/block/modal';
import { Icon } from '@/components/block/icon';
import { InputCheckbox} from "../../form/input-checkbox";
import { InputTextarea } from '@/components/form/input-textarea';
import { __ } from '@/helpers';
import { Button } from '@/components/block/button';
import {useToast} from "vue-toastification";

const props = defineProps({
  user: Object
});

const emits = defineEmits(['cancel']);

const openModal = ref(false);
const description = ref('');
const isAnonymous = ref(false);
const errors = ref({});
const isLoading = ref(false);

const toast = useToast();

function handleClose() {
  openModal.value = false;
  description.value = '';
  isAnonymous.value = false;
  errors.value = {};
}

function handleCancelButtonClick() {
  emits('cancel');
  handleClose();
}

async function handleSubmit() {
  errors.value = {};
  let firstElement = null;

  if (!description.value.trim()) {
    errors.value.description = [__('This field is required')];
    const element = document.getElementById('description');
    if (element && true) {
      firstElement = element;
    }
  }

  if (Object.keys(errors.value).length > 0) {
    if (firstElement) {
      firstElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    return;
  }

  try {
    isLoading.value = true;

    const payload = {
      description: description.value.trim(),
      isAnonymous: isAnonymous.value,  // <- Always include this
      ...(isAnonymous.value ? {} : { user: props.user })
    };

    const response = await axios.post(route('report-misconduct'), payload);

    if (response?.data?.message) {
      toast.success(response.data.message);
    }

    handleClose();
  } catch (error) {
    if (error?.response?.data?.errors) {
      errors.value = error.response.data.errors;
    } else {
      toast.error(__('An unexpected error has occurred. Please try again later.'));
    }
  } finally {
    isLoading.value = false;
  }
}
</script>

